import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fmtDate } from "@/lib/api";
import { can } from "@/lib/rbac";
import { useAuth } from "@/context/AuthContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageHead, EmptyState } from "@/components/Bits";
import { WarrantyRegister, WarrantyRowActions } from "@/components/WarrantyRegister";
import { WarrantyTasks } from "@/components/WarrantyTasks";
import { FreeEntryButton } from "@/components/StandaloneEntry";
import { WarrantyEditActions, WarrantyEditApprovals } from "@/components/WarrantyEditActions";
import { usePaged, ShowMore } from "@/lib/usePaged";
import { ExportButtons } from "@/components/ExportButtons";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const COLUMNS = [
  { label: "Certificate", value: (w) => w.certificate_no },
  { label: "Customer", value: (w) => w.customer_name },
  { label: "Studio", value: (w) => w.studio_name },
  { label: "Type", value: (w) => w.warranty_type },
  { label: "Start", value: (w) => w.start_date },
  { label: "Months", value: (w) => w.duration_months },
  { label: "Expiry", value: (w) => w.expiry_date },
  { label: "Acknowledged", value: (w) => (w.customer_ack ? "Yes" : "No") },
  { label: "Assigned CRM staff", value: (w) => w.assigned_crm_staff_name || "Unassigned" },
];

export default function Warranty() {
  const { user } = useAuth();
  const canAssign = can(user, "users:write");
  const [staff, setStaff] = useState([]);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [before, setBefore] = useState("");
  const [studioFilter, setStudioFilter] = useState("all");
  const [registerOpen, setRegisterOpen] = useState(false);
  const [regProduct, setRegProduct] = useState("PPF");
  const [studios, setStudios] = useState([]);
  const canRegister = can(user, "warranty:write");

  useEffect(() => {
    if (canRegister) api.get("/studios").then(({ data }) => setStudios(data)).catch(() => {});
  }, [canRegister]);

  const load = useCallback(() => {
    const params = {};
    if (search.trim()) params.search = search.trim();
    if (before) params.expiring_before = before;
    if (studioFilter && studioFilter !== "all") params.studio_id = studioFilter;
    api.get("/warranties", { params }).then(({ data }) => setRows(data));
  }, [search, before, studioFilter]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    if (canAssign) api.get("/crm-staff").then(({ data }) => setStaff(data)).catch(() => {});
  }, [canAssign]);

  const assign = async (w, staffId) => {
    try {
      await api.put(`/warranties/assign/${w.job_id}`, { staff_id: staffId });
      toast.success("Warranty reassigned");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const paged = usePaged(rows);
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div>
      <PageHead title="Warranties" subtitle="Registered PPF, ceramic and wrap warranties">
        {canRegister && (
          <Button
            data-testid="warranty-register-open-btn"
            className="min-h-[44px]"
            onClick={() => { setRegProduct("PPF"); setRegisterOpen(true); }}
          >
            <ShieldCheck className="mr-2 h-4 w-4" /> Register PPF warranty
          </Button>
        )}
        {canRegister && (
          <Button
            data-testid="warranty-register-velotint-btn"
            variant="outline"
            className="min-h-[44px]"
            onClick={() => { setRegProduct("Velotint Sunfilm"); setRegisterOpen(true); }}
          >
            <ShieldCheck className="mr-2 h-4 w-4" /> Register Velotint warranty
          </Button>
        )}
        {["network_admin", "ceo", "crm"].includes(user.role) && (
          <>
            <FreeEntryButton kind="warranty" studios={studios} onDone={load} lockProduct="PPF" />
            <FreeEntryButton kind="warranty" studios={studios} onDone={load} lockProduct="Velotint Sunfilm" />
          </>
        )}
        <ExportButtons testidPrefix="warranties" filename="veloskin-warranties" columns={COLUMNS} rows={rows} title="Warranties" />
      </PageHead>

      {["crm", "network_admin", "ceo"].includes(user.role) && (
        <div className="mb-6" data-testid="warranty-pending-section">
          <WarrantyTasks />
        </div>
      )}

      <WarrantyRegister
        open={registerOpen}
        onOpenChange={setRegisterOpen}
        studios={studios}
        onDone={load}
        lockProduct={regProduct}
      />

      {can(user, "warranty:approve") && (
        <Card className="mb-4 border-brand-line shadow-none" data-testid="warranty-approvals-card">
          <CardContent className="p-4">
            <h3 className="mb-3 flex items-center gap-2 text-base font-semibold text-brand-ink">
              Pending edit approvals
            </h3>
            <WarrantyEditApprovals />
          </CardContent>
        </Card>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="warranty-search-input"
            className="min-h-[44px] pl-9"
            placeholder="Customer, certificate or registration no."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {user.is_network && studios.length > 0 && (
          <Select value={studioFilter} onValueChange={setStudioFilter}>
            <SelectTrigger className="min-h-[44px] w-[200px]" data-testid="warranty-studio-filter">
              <SelectValue placeholder="All studios" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All studios</SelectItem>
              {studios.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <div>
          <Input
            type="date"
            data-testid="warranty-expiry-filter"
            className="min-h-[44px] w-[190px]"
            value={before}
            onChange={(e) => setBefore(e.target.value)}
          />
        </div>
        <span className="text-sm text-slate-500" data-testid="warranty-count">
          {rows.length} warranties
        </span>
      </div>

      <Card className="border-brand-line shadow-none">
        <CardContent className="overflow-x-auto p-0">
          <Table data-testid="warranties-table" className="mobile-cards">
            <TableHeader>
              <TableRow>
                <TableHead>Certificate</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Studio</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Start</TableHead>
                <TableHead>Expiry</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Assigned CRM staff</TableHead>
                <TableHead>Certificate PDF</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="p-0">
                    <EmptyState
                      icon={ShieldCheck}
                      testid="warranty-empty-state"
                      title="No warranties registered yet"
                      hint="Warranty certificates are created when a job with a warranty-backed service is delivered. Deliver a PPF, ceramic or wrap job to see it here."
                    />
                  </TableCell>
                </TableRow>
              )}
              {paged.visible.map((w) => {
                const expired = w.expiry_date < today;
                return (
                  <TableRow key={w.id} className="hover:bg-brand-tint/60" data-testid={`warranty-row-${w.id}`}>
                    <TableCell data-label="Certificate" className="font-mono text-xs font-semibold">
                      {w.job_id ? (
                        <Link to={`/jobs/${w.job_id}`} className="hover:text-crimson-600">
                          {w.certificate_no}
                        </Link>
                      ) : (
                        <span title="Standalone free-entry record">
                          {w.certificate_no}
                          <span className="ml-1 rounded bg-amber-100 px-1 py-0.5 text-[9px] font-semibold uppercase text-amber-700">
                            free
                          </span>
                        </span>
                      )}
                    </TableCell>
                    <TableCell data-label="Customer" className="font-medium">
                      {w.customer_name}
                    </TableCell>
                    <TableCell data-label="Studio" className="text-slate-500">
                      {w.studio_name}
                    </TableCell>
                    <TableCell data-label="Type">
                      <Badge className="border-0 bg-brand-tint text-brand">{w.warranty_type}</Badge>
                    </TableCell>
                    <TableCell data-label="Start" className="text-slate-500">
                      {fmtDate(w.start_date)}
                    </TableCell>
                    <TableCell data-label="Expiry" className="text-slate-700">
                      {fmtDate(w.expiry_date)}
                    </TableCell>
                    <TableCell data-label="Status">
                      <div className="flex flex-col gap-1">
                        <span
                          className={`flex items-center gap-1.5 text-xs font-semibold ${
                            expired ? "text-crimson-600" : "text-emerald-600"
                          }`}
                        >
                          <ShieldCheck className="h-3.5 w-3.5" />
                          {expired ? "Expired" : `${w.duration_months} mo active`}
                        </span>
                        <Badge
                          data-testid={`warranty-lifecycle-${w.id}`}
                          className={`w-fit border-0 ${
                            w.send_status === "sent" || (!w.send_status && w.emailed_at)
                              ? "bg-emerald-50 text-emerald-700"
                              : "bg-amber-100 text-amber-900"
                          }`}
                        >
                          {w.send_status === "sent" || (!w.send_status && w.emailed_at)
                            ? "Approved · Sent"
                            : "Registered"}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell data-label="Assigned CRM staff">
                      {canAssign && staff.length > 0 ? (
                        <Select
                          value={w.assigned_crm_staff || ""}
                          onValueChange={(v) => assign(w, v)}
                        >
                          <SelectTrigger
                            className="min-h-[44px] w-[170px]"
                            data-testid={`assign-crm-${w.id}`}
                          >
                            <SelectValue placeholder="Unassigned" />
                          </SelectTrigger>
                          <SelectContent>
                            {staff.map((st) => (
                              <SelectItem key={st.id} value={st.id}>
                                {st.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-sm text-slate-600">
                          {w.assigned_crm_staff_name || "Unassigned"}
                        </span>
                      )}
                    </TableCell>
                    <TableCell data-label="Certificate PDF">
                      <div className="flex items-center gap-1">
                        <WarrantyRowActions warranty={w} onChanged={load} />
                        <WarrantyEditActions warranty={w} onChanged={load} />
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <ShowMore paged={paged} testid="warranties-show-more" />
        </CardContent>
      </Card>
    </div>
  );
}
